/* Place app launchers on opposite sides above Ella's bottom bars; never alter dialogs. */
(() => {
  const BAR_SELECTOR = 'sticky-atc.sticky-atc, toolbar-mobile.toolbar-mobile';
  const HOST_SELECTOR = 'div.widerruf-popup-source';
  const BUTTON_SELECTOR = '.widerruf-trigger-button--floating';
  const CHAT_SELECTOR = 'shopify-chat';
  const WATCH_SELECTOR = `${BAR_SELECTOR}, ${HOST_SELECTOR}, ${CHAT_SELECTOR}`;
  const CHAT_PROPERTY = '--snt-chat-clearance';
  const LAUNCHER_VISIBILITY = 'var(--snt-app-launcher-visibility, visible)';
  let stop;

  function start() {
    if (stop) stop();
    const rootStyle = document.documentElement.style;
    const originalChat = rootStyle.getPropertyValue(CHAT_PROPERTY);
    const originalChatPriority = rootStyle.getPropertyPriority(CHAT_PROPERTY);
    const bars = new Set();
    const hosts = new Map();
    const buttons = new Map();
    let frame = 0;
    let fallbackTimer = 0;
    let retryTimer = 0;
    let attempts = 0;
    let stopped = false;

    function restore(style, property, value, priority) {
      if (value) style.setProperty(property, value, priority);
      else style.removeProperty(property);
    }

    function visibleRect(element, managedLauncher = false) {
      if (!element.isConnected || element.hidden) return null;
      const style = window.getComputedStyle(element);
      // Keep managed launcher geometry while the inherited drawer variable hides it.
      const managedVisibility = managedLauncher && element.style.getPropertyValue('visibility') === LAUNCHER_VISIBILITY;
      if (style.display === 'none' || (!managedVisibility &&
          (style.visibility === 'hidden' || style.visibility === 'collapse')) || Number(style.opacity) === 0) return null;
      const rect = element.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0 || rect.bottom <= 0 ||
          rect.top >= window.innerHeight || rect.right <= 0 ||
          rect.left >= window.innerWidth) return null;
      return rect;
    }

    function update() {
      frame = 0;
      if (stopped) return;
      let occupied = 0;
      for (const bar of bars) {
        if (!bar.isConnected) {
          resizeObserver?.unobserve(bar);
          bar.removeEventListener('transitionstart', schedule);
          bar.removeEventListener('transitionend', schedule);
          bars.delete(bar);
          continue;
        }
        const rect = visibleRect(bar);
        if (!rect) continue;
        const position = window.getComputedStyle(bar).position;
        if (position !== 'fixed' && position !== 'sticky') continue;
        // Full bar height also protects the launcher during the entrance transition.
        let height = Math.max(rect.height, window.innerHeight - rect.top);
        if (bar.matches('toolbar-mobile') && window.innerWidth <= 1024) {
          height = Math.max(54, height);
        }
        occupied = Math.max(occupied, height);
      }

      const clearance = Math.max(20, occupied > 0 ? Math.ceil(occupied + 16) : 0);
      for (const [button, original] of buttons) {
        if (!button.isConnected) {
          for (const property of ['bottom', 'left', 'right', 'visibility']) {
            restore(button.style, property, original[property].value, original[property].priority);
          }
          resizeObserver?.unobserve(button);
          buttons.delete(button);
          continue;
        }
        const rect = visibleRect(button, true);
        if (!rect) {
          if (original.applied) {
            for (const property of ['bottom', 'left', 'right', 'visibility']) {
              restore(button.style, property, original[property].value, original[property].priority);
            }
            original.applied = false;
          }
          continue;
        }
        const positions = {
          bottom: `calc(${clearance}px + env(safe-area-inset-bottom, 0px))`,
          left: '16px',
          right: 'auto',
          visibility: LAUNCHER_VISIBILITY,
        };
        for (const [property, value] of Object.entries(positions)) {
          if (button.style.getPropertyValue(property) !== value ||
              button.style.getPropertyPriority(property) !== 'important') {
            button.style.setProperty(property, value, 'important');
          }
        }
        original.applied = true;
      }

      const value = `calc(${clearance}px + env(safe-area-inset-bottom, 0px))`;
      if (rootStyle.getPropertyValue(CHAT_PROPERTY) !== value) {
        rootStyle.setProperty(CHAT_PROPERTY, value);
      }
    }

    function runScheduledUpdate() {
      if (stopped || (!frame && !fallbackTimer)) return;
      window.cancelAnimationFrame(frame);
      window.clearTimeout(fallbackTimer);
      frame = 0;
      fallbackTimer = 0;
      if (!stopped) update();
    }

    function schedule() {
      if (stopped || frame || fallbackTimer) return;
      // A background page may not receive animation frames before its first interaction.
      frame = window.requestAnimationFrame(runScheduledUpdate);
      fallbackTimer = window.setTimeout(runScheduledUpdate, 100);
    }

    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;
    const barObserver = new MutationObserver(schedule);

    function discoverButton(host, state) {
      if (stopped || !host.isConnected) return;
      const shadow = host.shadowRoot;
      if (!shadow) return;
      if (state.shadow !== shadow) {
        state.shadowObserver?.disconnect();
        state.shadow = shadow;
        state.shadowObserver = new MutationObserver(() => {
          discoverButton(host, state);
          schedule();
        });
        state.shadowObserver.observe(shadow, { childList: true, subtree: true });
      }
      for (const button of shadow.querySelectorAll(BUTTON_SELECTOR)) {
        if (!buttons.has(button)) {
          const original = { applied: false };
          for (const property of ['bottom', 'left', 'right', 'visibility']) {
            original[property] = {
              value: button.style.getPropertyValue(property),
              priority: button.style.getPropertyPriority(property),
            };
          }
          buttons.set(button, original);
          resizeObserver?.observe(button);
        }
      }
      schedule();
    }

    function discover() {
      if (stopped) return;
      for (const bar of document.querySelectorAll(BAR_SELECTOR)) {
        if (bars.has(bar)) continue;
        bars.add(bar);
        resizeObserver?.observe(bar);
        // Observe only native bars, never all page classes or app modal attributes.
        barObserver.observe(bar, { attributes: true, attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'] });
        bar.addEventListener('transitionstart', schedule);
        bar.addEventListener('transitionend', schedule);
      }
      for (const host of document.querySelectorAll(HOST_SELECTOR)) {
        let state = hosts.get(host);
        if (!state) {
          state = {};
          state.readyObserver = new MutationObserver(() => discoverButton(host, state));
          state.readyObserver.observe(host, { attributes: true, attributeFilter: ['data-widerruf-popup-shadow-ready'] });
          hosts.set(host, state);
        }
        discoverButton(host, state);
      }
      for (const [host, state] of hosts) {
        if (host.isConnected) continue;
        state.readyObserver.disconnect();
        state.shadowObserver?.disconnect();
        hosts.delete(host);
      }
      schedule();
    }

    const bodyObserver = new MutationObserver((records) => {
      if (records.some((record) => [...record.addedNodes, ...record.removedNodes].some((node) =>
        node.nodeType === 1 && (node.matches(WATCH_SELECTOR) || node.querySelector(WATCH_SELECTOR))
      ))) discover();
    });
    bodyObserver.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    document.addEventListener('shopify:section:load', discover);
    document.addEventListener('shopify:section:unload', discover);

    // Shadow attachment itself emits no child-list event. Retry at most 20 times.
    function retryDiscovery() {
      discover();
      attempts += 1;
      if (attempts < 20 && !stopped) retryTimer = window.setTimeout(retryDiscovery, 500);
    }
    retryDiscovery();

    stop = () => {
      stopped = true;
      window.cancelAnimationFrame(frame);
      window.clearTimeout(fallbackTimer);
      window.clearTimeout(retryTimer);
      bodyObserver.disconnect();
      barObserver.disconnect();
      resizeObserver?.disconnect();
      for (const bar of bars) {
        bar.removeEventListener('transitionstart', schedule);
        bar.removeEventListener('transitionend', schedule);
      }
      for (const state of hosts.values()) {
        state.readyObserver.disconnect();
        state.shadowObserver?.disconnect();
      }
      for (const [button, original] of buttons) {
        if (original.applied) {
          for (const property of ['bottom', 'left', 'right', 'visibility']) {
            restore(button.style, property, original[property].value, original[property].priority);
          }
        }
      }
      restore(rootStyle, CHAT_PROPERTY, originalChat, originalChatPriority);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      document.removeEventListener('shopify:section:load', discover);
      document.removeEventListener('shopify:section:unload', discover);
      stop = null;
    };
  }

  window.addEventListener('pagehide', () => stop?.());
  window.addEventListener('pageshow', (event) => { if (event.persisted) start(); });
  start();
})();
